/* eslint-disable no-console */
import _ from 'lodash';
import { batch } from 'react-redux';

import { resetDashboardPartData } from './DashboardActions';
import { handleErrors } from './ErrorActions';
import * as appModuleApi from '../api/app-module';
import * as dashboardConfigApi from '../api/dashboard-config';
import {
  DASHBOARD_ADMINISTRATOR_RIGHTS_CODE,
  DEFAULT_DASHBOARD_KEY,
  GLOBAL_DASHBOARD_KEY,
} from '../containers/Dashboard/constants';
import { getCurrentLoadedDashboardInfo, getLoadedDashboardsInfo } from '../utils/dashboardUtils';

export const SET_DASHBOARD_PENDING = 'DashboardConfig/SET_DASHBOARD_PENDING';
export const FETCH_DASHBOARD_CONFIG_SUCCESS = 'DashboardConfig/FETCH_DASHBOARD_CONFIG_SUCCESS';
export const DASHBOARD_REQUEST_FAILURE = 'DashboardConfig/DASHBOARD_REQUEST_FAILURE';

export const SAVE_DASHBOARD_CONFIG_SUCCESS = 'DashboardConfig/SAVE_DASHBOARD_CONFIG_SUCCESS';

export const CHANGE_DASHBOARD_EDITING_STATE = 'DashboardConfig/CHANGE_DASHBOARD_EDITING_STATE';
export const DELETE_DASHBOARD_PART = 'DashboardConfig/DELETE_DASHBOARD_PART';
export const UPDATE_DASHBOARD_PART = 'DashboardConfig/UPDATE_DASHBOARD_PART';

export const EDIT_DASHBOARD_TAB = 'DashboardConfig/EDIT_DASHBOARD_TAB';
export const EDIT_DASHBOARD_TAB_CLOSE = 'DashboardConfig/EDIT_DASHBOARD_TAB_CLOSE';

const dashboardRequestFailure = () => ({
  type: DASHBOARD_REQUEST_FAILURE,
});

export const setDashboardPending = (flag) => ({
  type: SET_DASHBOARD_PENDING,
  payload: { flag },
});

const fetchDashboardConfigSuccess = (
  prevConfig,
  config,
  rowId,
  code,
  hasAdminRights,
  dashboardsInfo = {}
) => ({
  type: FETCH_DASHBOARD_CONFIG_SUCCESS,
  payload: { prevConfig, config, rowId, code, hasAdminRights, dashboardsInfo },
});

async function handleInstalledModules(json, dispatch) {
  const personalization = JSON.parse(json);
  if (personalization.active === 'standard') {
    // Check if TrainingManagement module is installed
    const trainingModuleResponse = await appModuleApi.moduleIsInstalled(
      appModuleApi.TRAINING_MANAGEMENT
    );

    if (trainingModuleResponse.hasError) {
      dispatch(handleErrors(trainingModuleResponse.errors));
    } else if (trainingModuleResponse.moduleIsInstalled) {
      personalization.active = 'training';
      return JSON.stringify(personalization);
    } else {
      // If the TrainingManagement module is not istalled, we also check for DocumentAndTrainingManagement
      const documentModuleResponse = await appModuleApi.moduleIsInstalled(
        appModuleApi.DOCUMENT_AND_TRAINING_MANAGEMENT
      );

      if (documentModuleResponse.hasError) {
        dispatch(handleErrors(documentModuleResponse.errors));
      } else if (documentModuleResponse.moduleIsInstalled) {
        personalization.active = 'training';
        return JSON.stringify(personalization);
      }
    }
  }

  return json;
}

export const fetchDashboardConfig = (params = { forceRefresh: false }) => async (
  dispatch,
  getState
) => {
  const { dashboardConfig } = getState();
  const { forceRefresh } = params;

  // Prevent undesired multiple dashboard config fetching
  if (dashboardConfig.entityTable && !forceRefresh) {
    return;
  }

  dispatch(setDashboardPending(true));

  try {
    const { auth } = getState();
    const { actorCode } = auth;

    let apiData = await dashboardConfigApi.getDashboardConfig(actorCode);

    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
      dispatch(dashboardRequestFailure());
      return;
    }

    const dataPath = ['entityDataStore', 'entityData', 'V_PER_PERSONALIZATION'];

    if (!_.get(apiData, dataPath)) {
      apiData = await dashboardConfigApi.getDashboardConfig(GLOBAL_DASHBOARD_KEY);
    }

    if (apiData?.hasError) {
      dispatch(handleErrors(apiData.errors));
      dispatch(dashboardRequestFailure());
      return;
    }

    if (!_.get(apiData, dataPath)) {
      apiData = await dashboardConfigApi.getDashboardConfig(DEFAULT_DASHBOARD_KEY);
    }

    const rightsPath = [
      'formInfo',
      'userContext',
      'f_CurrentRights',
      DASHBOARD_ADMINISTRATOR_RIGHTS_CODE,
    ];

    const personalization = _.get(apiData, dataPath);
    const hasAdminRights = _.get(apiData, rightsPath, false);
    const prevConfig = _.get(personalization, 'PER_DASHBOARD_OPTIONS');
    const config = await handleInstalledModules(prevConfig, dispatch);
    const rowId = _.get(personalization, 'PK_ID');
    const code = _.get(personalization, 'CODE');

    let dashboardsInfo = getLoadedDashboardsInfo(code);

    // get global dashboard only for admin if global dashboard was not fetched before
    if (hasAdminRights && dashboardsInfo.userSpecificDashboard) {
      const globalDashboardData = await dashboardConfigApi.getDashboardConfig(GLOBAL_DASHBOARD_KEY);
      dashboardsInfo = {
        ...dashboardsInfo,
        globalDefaultDashboard: !!_.get(globalDashboardData, dataPath),
      };
    }

    dispatch(
      fetchDashboardConfigSuccess(prevConfig, config, rowId, code, hasAdminRights, dashboardsInfo)
    );
  } catch (error) {
    console.log(`fetchDashboardConfig() failed: ${JSON.stringify(error)}`);
    dispatch(handleErrors(error));
    dispatch(dashboardRequestFailure());
  }
};

export const fetchDashboardConfigByKey = (key) => async (dispatch) => {
  dispatch(setDashboardPending(true));

  try {
    const apiData = await dashboardConfigApi.getDashboardConfig(key);

    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
      dispatch(dashboardRequestFailure());
      return;
    }

    const dataPath = ['entityDataStore', 'entityData', 'V_PER_PERSONALIZATION'];
    const personalization = _.get(apiData, dataPath);

    const rightsPath = [
      'formInfo',
      'userContext',
      'f_CurrentRights',
      DASHBOARD_ADMINISTRATOR_RIGHTS_CODE,
    ];
    const hasAdminRights = _.get(apiData, rightsPath, false);

    const prevConfig = _.get(personalization, 'PER_DASHBOARD_OPTIONS');
    const config = await handleInstalledModules(prevConfig, dispatch);
    const rowId = _.get(personalization, 'PK_ID');
    const code = _.get(personalization, 'CODE');

    dispatch(
      fetchDashboardConfigSuccess(
        prevConfig,
        config,
        rowId,
        code,
        hasAdminRights,
        getCurrentLoadedDashboardInfo(code)
      )
    );
  } catch (error) {
    console.log(`fetchDashboardConfigByKey() failed: ${JSON.stringify(error)}`);
    dispatch(handleErrors(error));
    dispatch(dashboardRequestFailure());
  }
};

const saveDashboardConfigSuccess = (config, code, rowId) => ({
  type: SAVE_DASHBOARD_CONFIG_SUCCESS,
  payload: { config, code, rowId },
});

export const saveDashboardConfig = ({ updates, isEditGlobal } = {}) => async (
  dispatch,
  getState
) => {
  try {
    const { auth, dashboardConfig } = getState();
    const { actorCode: currentActorCode } = auth;
    const { prevConfig, code, rowId, personalization } = dashboardConfig;
    const { active } = personalization;
    const data = updates || _.get(personalization, ['draft', active]);

    const newConfig = JSON.stringify({
      mode: 'general',
      active,
      config: {
        ...personalization.config,
        [active]: { ...data },
      },
    });

    const actorCode = isEditGlobal ? GLOBAL_DASHBOARD_KEY : currentActorCode;
    const apiData = await dashboardConfigApi.saveDashboardConfig(
      prevConfig,
      newConfig,
      actorCode,
      code,
      rowId
    );

    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
      dispatch(dashboardRequestFailure());
      return;
    }

    batch(() => {
      dispatch(saveDashboardConfigSuccess(data, actorCode, rowId));
      dispatch(resetDashboardPartData());

      if (isEditGlobal) {
        dispatch(fetchDashboardConfigByKey(GLOBAL_DASHBOARD_KEY));
      } else {
        dispatch(fetchDashboardConfig({ forceRefresh: true }));
      }
    });
  } catch (error) {
    console.log(`saveDashboardConfig() failed: ${JSON.stringify(error)}`);
    dispatch(handleErrors(error));
    dispatch(dashboardRequestFailure());
  }
};

export const deleteDashboard = (code) => async (dispatch) => {
  dispatch(setDashboardPending(true));

  try {
    const apiData = await dashboardConfigApi.deleteDashboardConfig(code);

    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
      dispatch(dashboardRequestFailure());
      return;
    }

    dispatch(fetchDashboardConfig());
  } catch (error) {
    console.log(`deleteDashboard() failed: ${JSON.stringify(error)}`);
    dispatch(handleErrors(error));
    dispatch(dashboardRequestFailure());
  }
};

export const handleRequestCancel = ({
  action,
  dashboardCode,
  hasAdminRights,
  userSpecificDashboard,
  actorCode,
  isEditGlobal,
}) => async (dispatch) => {
  if (!action) {
    return;
  }

  if (
    hasAdminRights &&
    isEditGlobal &&
    userSpecificDashboard &&
    dashboardCode === DEFAULT_DASHBOARD_KEY
  ) {
    await dispatch(fetchDashboardConfigByKey(actorCode));
  }

  dispatch(changeDashboardEditingState(action));
};

export const changeDashboardEditingState = (value, isEditGlobal) => ({
  type: CHANGE_DASHBOARD_EDITING_STATE,
  value,
  isEditGlobal,
});

export const deleteDashboardPart = (id) => ({
  type: DELETE_DASHBOARD_PART,
  id,
});

export const updateDashboardPart = (id, updates) => ({
  type: UPDATE_DASHBOARD_PART,
  payload: { id, updates },
});

export const editDashboardTab = (id) => ({
  type: EDIT_DASHBOARD_TAB,
  payload: { id },
});

export const editDashboardTabClose = () => ({
  type: EDIT_DASHBOARD_TAB_CLOSE,
});
